/**
 * Self-hosted image optimization system
 * Replaces Vercel's image optimization to avoid quota limits
 */

import { isSupabaseStorageUrl } from './image-optimization'

interface ImageOptimizationOptions {
  width?: number
  height?: number
  quality?: number
  format?: 'webp' | 'avif' | 'jpeg' | 'png'
}

/**
 * Generate optimized image URL using our own systems
 * This completely bypasses Vercel's image optimization
 */
export function generateSelfHostedImageUrl(
  src: string,
  options: ImageOptimizationOptions = {}
): string {
  // For local images, return as-is (no optimization needed)
  if (src.startsWith('/')) {
    return src
  }

  // For Supabase images, use our proxy
  if (isSupabaseStorageUrl(src)) {
    const params = new URLSearchParams({
      url: encodeURIComponent(src),
      f: options.format || 'webp',
      q: (options.quality || 85).toString()
    })

    if (options.width) params.set('w', options.width.toString())
    if (options.height) params.set('h', options.height.toString())

    return `/api/image-proxy?${params.toString()}`
  }

  // For external images, return as-is (or could add another proxy)
  return src
}

/**
 * Generate responsive image srcset without Vercel optimization
 */
export function generateSelfHostedSrcSet(
  src: string,
  sizes: number[],
  options: Omit<ImageOptimizationOptions, 'width'> = {}
): string {
  if (src.startsWith('/')) {
    // For local images, just return the original for all sizes
    return sizes.map(size => `${src} ${size}w`).join(', ')
  }

  if (isSupabaseStorageUrl(src)) {
    return sizes
      .map(size => {
        const optimizedUrl = generateSelfHostedImageUrl(src, {
          ...options,
          width: size
        })
        return `${optimizedUrl} ${size}w`
      })
      .join(', ')
  }

  // For external images, return original for all sizes
  return sizes.map(size => `${src} ${size}w`).join(', ')
}

/**
 * Preload critical images using our self-hosted system
 */
export function preloadSelfHostedImages(
  urls: string[],
  options: ImageOptimizationOptions = {}
): void {
  urls.forEach(url => {
    const optimizedUrl = generateSelfHostedImageUrl(url, options)
    
    const link = document.createElement('link')
    link.rel = 'preload'
    link.as = 'image'
    link.href = optimizedUrl
    
    if (options.format) {
      link.type = `image/${options.format}`
    }
    
    document.head.appendChild(link)
  })
}

/**
 * Check if we're using self-hosted optimization
 */
export function isSelfHostedOptimization(): boolean {
  return true // Always true since we disabled Vercel optimization
}

/**
 * Get optimization status and quota info
 */
export function getOptimizationStatus() {
  return {
    provider: 'self-hosted',
    quotaUsed: 0,
    quotaLimit: Infinity,
    quotaRemaining: Infinity,
    costPerOptimization: 0,
    features: [
      'Unlimited optimizations',
      'No quota limits',
      'Self-hosted processing',
      'Privacy compliant',
      'Supabase image proxy',
      'WebP/AVIF support'
    ]
  }
}